import React, { useEffect } from "react";
import { useImmer } from "use-immer";
import { errorMessage } from "../../utils";
import {
  getAccounts,
  getAvailabilityGroupsList,
  getCategories,
  getCustomers,
  getFranchiseesList,
  getKeypads,
  getModifierGroups,
  getPriceLevels,
  getProductsList,
  getRoles,
  getStates,
  getSuburbs,
  getStore,
  getTaxGroups,
  getTerminals,
  getUsers,
  getBundleGroupsList,
  getCoupon,
  getGiftCardUsers,
  getStampCards,
  getSegmentsList,
} from "../api";
import qs from "qs";
import { useDebounce } from "use-debounce";
import { getReportSettingsListApi } from "../../modules";
import { type } from "@testing-library/user-event/dist/type";

export const useDropdownApis = ({
  isStoreList,
  isCategoryList,
  isProductsList,
  isModifierGroupList,
  isAvailabilityGroupList,
  isAccountsList,
  isFranchiseeList,
  isStatesList,
  isSuburbList,
  isTerminalsList,
  isKeypadList,
  isTaxgroupList,
  isPricelevelsList,
  isRolesList,
  isCustomersList,
  isUsersList,
  isBundle,
  isBundleGroupList,
  isTerminalList,
  isFranchiseeWiseStoreList,
  isStateWiseStoreList,
  isReportSettingsList,
}) => {
  const [state, setState] = useImmer({
    franchiseeWiseStores: [],
    stores: [],
    categories: [],
    products: [],
    modifierGroups: [],
    availabilityGroups: [],
    accounts: [],
    franchisees: [],
    states: [],
    suburbs: [],
    terminals: [],
    keypads: [],
    taxgroups: [],
    pricelevels: [],
    roles: [],
    customers: [],
    users: [],
    giftCardUsers: [],
    coupons: [],
    stampCards: [],
    product: {
      dataList: [],
      searchText: "",
      pagination: {
        pageIndex: 1,
        pageSize: 10,
        pageCount: 0,
        total: 0,
        hasMorePages: true,
        lastPage: 1,
      },
    },
    bundleGroups: [],
    reportSettingsList: [],
  });
  const [debouncedSearchText] = useDebounce(state.product.searchText, 1000);

  useEffect(() => {
    if (isStoreList || isFranchiseeWiseStoreList || isStateWiseStoreList) {
      handleGetStores(
        isFranchiseeWiseStoreList
          ? "franchisee"
          : isStateWiseStoreList
            ? "state"
            : "",
      );
    }
    if (isCategoryList) {
      handleGetCategories();
    }
    if (isProductsList) {
      handleGetProducts();
    }
    if (isModifierGroupList) {
      handleGetModifierGroups();
    }
    if (isAvailabilityGroupList) {
      handleGetAvailabilityGroups();
    }
    if (isAccountsList) {
      handleGetAccounts();
    }
    if (isFranchiseeList) {
      handleGetFranchisees();
    }
    if (isStatesList) {
      handleGetStates();
    }
    if (isTerminalsList) {
      handleGetTerminals();
    }
    if (isKeypadList) {
      handleGetKeypads();
    }
    if (isTaxgroupList) {
      handleGetTaxgroups();
    }
    if (isPricelevelsList) {
      handleGetPriceLevels();
    }
    if (isRolesList) {
      handleGetRoles();
    }
    if (isCustomersList) {
      handleGetCustomers();
    }
    if (isUsersList) {
      handleGetUsers();
    }
    if (isBundleGroupList) {
      handleGetBundleGroups();
    }
    if (isReportSettingsList) {
      handleGetReportSettings();
    }
  }, [
    isFranchiseeWiseStoreList,
    isStoreList,
    isCategoryList,
    isProductsList,
    isModifierGroupList,
    isAvailabilityGroupList,
    isAccountsList,
    isFranchiseeList,
    isStatesList,
    isSuburbList,
    isTerminalsList,
    isKeypadList,
    isTaxgroupList,
    isPricelevelsList,
    isRolesList,
    isCustomersList,
    isUsersList,
    isBundleGroupList,
  ]);

  const handleGetStores = async (type = "") => {
    try {
      const params = {};
      if (type === "franchisee") {
        params.franchisee = 1;
      } else if (type === "state") {
        params.state = 1;
      }
      const res = await getStore(params);
      if (res.success) {
        setState((draft) => {
          draft.stores = res.data;
          if (type === "franchisee") {
            draft.franchiseeWiseStores = res.data;
          }
        });
      }
    } catch (err) {
      errorMessage(err.response.data.message);
    }
  };
  const handleGetCategories = async () => {
    try {
      const res = await getCategories();
      if (res.success) {
        setState((draft) => {
          draft.categories = res.data;
        });
      }
    } catch (err) {
      errorMessage(err.response.data.message);
    }
  };
  const handleGetProducts = async () => {
    try {
      const res = await getProductsList();
      if (res.success) {
        setState((draft) => {
          draft.products = res.data;
        });
      }
    } catch (err) {
      errorMessage(err.response.data.message);
    }
  };
  const handleGetModifierGroups = async () => {
    try {
      const res = await getModifierGroups();
      if (res.success) {
        setState((draft) => {
          draft.modifierGroups = res.data;
        });
      }
    } catch (err) {
      errorMessage(err.response.data.message);
    }
  };
  const handleGetAvailabilityGroups = async () => {
    try {
      const res = await getAvailabilityGroupsList();
      if (res.success) {
        setState((draft) => {
          draft.availabilityGroups = res.data;
        });
      }
    } catch (err) {
      errorMessage(err.response.data.message);
    }
  };
  const handleGetBundleGroups = async () => {
    try {
      const res = await getBundleGroupsList();
      if (res.success) {
        setState((draft) => {
          draft.bundleGroups = res.data;
        });
      }
    } catch (err) {
      errorMessage(err.response.data.message);
    }
  };
  const handleGetAccounts = async () => {
    try {
      const res = await getAccounts();
      if (res.success) {
        setState((draft) => {
          draft.accounts = res.data;
        });
      }
    } catch (err) {
      errorMessage(err.response.data.message);
    }
  };
  const handleGetFranchisees = async () => {
    try {
      const res = await getFranchiseesList();
      if (res.success) {
        setState((draft) => {
          draft.franchisees = res.data;
        });
      }
    } catch (err) {
      errorMessage(err.response.data.message);
    }
  };
  const handleGetStates = async () => {
    try {
      const res = await getStates();
      if (res.success) {
        setState((draft) => {
          draft.states = res.data;
        });
      }
    } catch (err) {
      errorMessage(err.response.data.message);
    }
  };
  const handleGetTerminals = async () => {
    try {
      const res = await getTerminals();
      if (res.success) {
        setState((draft) => {
          draft.terminals = res.data;
        });
      }
    } catch (err) {
      errorMessage(err.response.data.message);
    }
  };
  const handleGetKeypads = async () => {
    try {
      const res = await getKeypads();
      if (res.success) {
        setState((draft) => {
          draft.keypads = res.data;
        });
      }
    } catch (err) {
      errorMessage(err.response.data.message);
    }
  };
  const handleGetTaxgroups = async () => {
    try {
      const res = await getTaxGroups();
      if (res.success) {
        setState((draft) => {
          draft.taxgroups = res.data.map((v) => {
            return { value: v.id, label: v.name };
          });
        });
      }
    } catch (err) {
      errorMessage(err.response.data.message);
    }
  };
  const handleGetPriceLevels = async () => {
    try {
      const res = await getPriceLevels();
      if (res.success) {
        setState((draft) => {
          draft.pricelevels = res.data;
        });
      }
    } catch (err) {
      errorMessage(err.response.data.message);
    }
  };
  const handleGetRoles = async () => {
    try {
      const res = await getRoles();
      if (res.success) {
        setState((draft) => {
          draft.roles = res.data;
        });
      }
    } catch (err) {
      errorMessage(err.response.data.message);
    }
  };

  const handleGetCustomers = async ({
    search = null,
    page,
    is_deleted_user_list = 0,
  }) => {
    let params = {};
    let pagination = {
      pageIndex: page,
      pageSize: 10,
    };
    if (search) {
      params = { ...params, search };
    }
    if (is_deleted_user_list) {
      params = { ...params, not_deleted_user_search: is_deleted_user_list };
    }

    const query = {
      params,
      paramsSerializer: (params) => qs.stringify(params),
    };
    try {
      const res = await getCustomers(query, pagination);
      if (res.success) {
        return {
          options: res.data.customers.map((data) => {
            return {
              value: data.id,
              label: data.name,
              email: data.email,
              phone: data.phone_formatted,
            };
          }),
          hasMore: res.data.pagination.hasMorePages,
          additional: {
            page: page + 1,
          },
        };
      }
    } catch (err) {
      errorMessage(err.response.data.message);
      return {
        options: [],
        hasMore: false,
        additional: {
          page: page,
        },
      };
    }
  };

  const handleGetUsers = async ({ search = null, page, type }) => {
    let params = {};
    let pagination = {
      pageIndex: page,
      pageSize: 10,
    };
    if (search) {
      params = { ...params, search };
    }
    const query = {
      params,
      paramsSerializer: (params) => qs.stringify(params),
    };
    try {
      const res = await getUsers(query, pagination);

      if (res.success) {
        setState((draft) => {
          draft.users = res.data.users.map((data) => {
            return { value: data.full_name, label: data.full_name };
          });
        });
        const options = res.data.users.map((data) => {
          return {
            value: data.id,
            label: data.full_name,
          };
        });
        return {
          options: options,
          hasMore: res.data.pagination.hasMorePages,
          additional: {
            page: page + 1,
          },
        };
      }
    } catch (err) {
      return {
        options: [],
        hasMore: false,
        additional: {
          page: page,
        },
      };
    }
  };

  const handleGetGiftCardUsers = async ({ search = null, page, type }) => {
    let params = {};
    let pagination = {
      pageIndex: page,
      pageSize: 10,
    };
    if (search) {
      params = { ...params, search };
    }
    const query = {
      params,
      paramsSerializer: (params) => qs.stringify(params),
    };
    try {
      const res = await getGiftCardUsers(query, pagination);

      if (res.success) {
        setState((draft) => {
          draft.giftCardUsers = res.data.users.map((data) => {
            return { value: data.full_name, label: data.full_name };
          });
        });
        const options = res.data.users.map((data) => {
          return {
            value: data.id,
            label: data.full_name,
          };
        });
        return {
          options: options,
          hasMore: res.data.pagination.hasMorePages,
          additional: {
            page: page + 1,
          },
        };
      }
    } catch (err) {
      return {
        options: [],
        hasMore: false,
        additional: {
          page: page,
        },
      };
    }
  };
  const handleSuburbList = async ({ search = null, page, type }) => {
    let params = {};
    let pagination = {
      pageIndex: page,
      pageSize: 10,
    };
    if (search) {
      params = { ...params, search };
    }
    const query = {
      params,
      paramsSerializer: (params) => qs.stringify(params),
    };
    try {
      const res = await getSuburbs(query, pagination);

      if (res.success) {
        setState((draft) => {
          draft.suburbs = res.data.suburbs.map((data) => {
            return { value: data.name, label: data.name };
          });
        });
        const options = res.data.suburbs.map((data) => {
          return {
            value: data.name,
            label: data.name,
            postcode: data.postcode,
            state: data.state,
          };
        });
        return {
          options: options,
          hasMore: res.data.pagination.hasMorePages,
          additional: {
            page: page + 1,
          },
        };
      }
    } catch (err) {
      return {
        options: [],
        hasMore: false,
        additional: {
          page: page,
        },
      };
    }
  };

  const handleGetProductsList = async ({
    search = null,
    page,
    type,
    excludeStatusZero = false,
  }) => {
    let params = {};
    let pagination = {
      pageIndex: page ?? 1,
      pageSize: 10,
    };
    if (search) {
      params = { ...params, search };
    }
    if (isBundle) {
      params = { ...params, product_types: ["retail"] };
    }

    const query = {
      params,
      paramsSerializer: (params) => qs.stringify(params),
    };

    try {
      const res = await getProductsList(query, pagination);
      if (res.success) {
        let products = res.data.products;

        // Filter out products with status 0 if excludeStatusZero is true
        if (excludeStatusZero) {
          products = products.filter((product) => product.status !== 0);
        }

        if (type === "search") {
          setState((draft) => {
            draft.product.dataList = [...products];
            draft.product.pagination = res.data.pagination;
          });
        } else {
          setState((draft) => {
            draft.product.dataList = [...draft?.product.dataList, ...products];
            draft.product.pagination = res.data.pagination;
          });
        }

        const options =
          products.length > 0
            ? products.map((data) => {
                return {
                  value: data.id,
                  label: data.name,
                  variants: data.variants,
                };
              })
            : [];

        return {
          options: options,
          hasMore: res.data.pagination.hasMorePages,
          additional: {
            page: page + 1,
          },
        };
      }
    } catch (err) {
      return {
        options: [],
        hasMore: false,
        additional: {
          page: page,
        },
      };
    }
  };

  const productLoadNextPage = (e) => {
    const { pageIndex } = state?.product.pagination;
    handleGetProductsList({
      page: pageIndex + 1,
      type: "",
      search: debouncedSearchText,
    });
    setState((draft) => {
      draft.product.pagination.pageIndex =
        state.product.pagination.pageIndex + 1;
    });
  };

  const HandleFilterProducts = (type, text) => {
    setState((draft) => {
      draft.product.searchText = text;
    });
  };

  useEffect(() => {
    if (isProductsList && (debouncedSearchText || debouncedSearchText === "")) {
      handleGetProductsList({
        page: 1,
        search: debouncedSearchText,
        type: "search",
      });
    }
  }, [debouncedSearchText]);

  const handleCouponList = async ({
    deletedItem,
    search,
    page,
    type,
    status,
  }) => {
    let params = {};
    let pagination = {
      pageIndex: page,
      pageSize: 10,
    };
    if (deletedItem !== null) {
      params = { ...params, deleted: deletedItem };
    }
    if (search) {
      params = { ...params, search: search };
    }
    if (type) {
      params = { ...params, coupon_type: type };
    }
    if (status) {
      params = { ...params, status: status };
    }
    const query = {
      params,
      paramsSerializer: (params) => qs.stringify(params),
    };
    try {
      const res = await getCoupon(query, pagination);

      if (res.success) {
        setState((draft) => {
          draft.coupons = res.data.coupons.map((data) => {
            return { value: data.id, label: data.name };
          });
        });
        const options = res.data.coupons.map((data) => {
          return {
            value: data.id,
            label: data.name,
            // postcode: data.postcode,
            // state: data.state,
          };
        });
        return {
          options: options,
          hasMore: res.data.pagination.hasMorePages,
          additional: {
            page: page + 1,
          },
        };
      }
    } catch (err) {
      return {
        options: [],
        hasMore: false,
        additional: {
          page: page,
        },
      };
    }
  };

  const handleStampCardList = async ({ search = null, page , stamp_card_id }) => {
    let params = {};
    const pagination = {
      pageIndex: page,
      pageSize: 10,
    };

    if (search) {
      params = { ...params, search };
    }
    if (stamp_card_id) {
      params = { ...params, stamp_card_id };
    }

    const query = {
      params,
      paramsSerializer: (params) => qs.stringify(params),
    };

    try {
      const res = await getStampCards(query, pagination);

      if (res?.success) {
        const list = res.data?.stamp_cards || [];

        setState((draft) => {
          draft.stampCards = list;
        });

        const options = list.map((item) => ({
          value: item.id,
          label: item.name,
        }));

        const paginationInfo = res.data?.pagination;

        return {
          options,
          hasMore: paginationInfo ? paginationInfo.hasMorePages : false,
          additional: {
            page: paginationInfo ? page + 1 : page,
          },
        };
      }
    } catch (err) {
      errorMessage(err?.response?.data?.message);
    }

    return {
      options: [],
      hasMore: false,
      additional: {
        page,
      },
    };
  };

  const handleSegmentList = async ({ search, page }) => {
    let params = {};
    const pagination = {
      pageIndex: page,
      pageSize: 10,
    };

    if (search) {
      params = { ...params, search };
    }

    const query = {
      params,
      paramsSerializer: (params) => qs.stringify(params),
    };

    try {
      const res = await getSegmentsList(query, pagination);

      if (res?.success) {
        const list = res.data?.segments || [];
        const options = list.map((item) => ({
          value: item.id,
          label: item.name,
          result_count: item.result_count,
        }));
        return {
          options,
          hasMore: res.data?.pagination?.hasMorePages ?? false,
          additional: {
            page: page + 1,
          },
        };
      }
    } catch (err) {
      errorMessage(err?.response?.data?.message);
    }

    return {
      options: [],
      hasMore: false,
      additional: { page },
    };
  };

  const handleGetReportSettings = async () => {
    try {
      const res = await getReportSettingsListApi();
      if (res.success) {
        setState((draft) => {
          draft.reportSettingsList = res.data;
        });
      }
    } catch (err) {
      console.error(err);
    }
  };

  return {
    state,
    statesList: state.states,
    franchisees: state.franchisees,
    suburbList: state.suburbs,
    storeList: state.stores,
    franchiseeWiseStoreList: state.franchiseeWiseStores,
    categoriesList: state.categories,
    pricelevelList: state.pricelevels,
    bundleGroupsList: state.bundleGroups,
    availabilityGroupList: state.availabilityGroups,
    modifierGroupsList: state.modifierGroups,
    coasList: state.accounts,
    taxGroupList: state.taxgroups,
    couponList: state.coupons,
    stampCardsList: state.stampCards,
    terminals: state.terminals,
    reportSettingsList: state.reportSettingsList,
    handleGetProductsList,
    productLoadNextPage,
    HandleFilterProducts,
    handleSuburbList,
    handleCouponList,
    handleStampCardList,
    handleGetCustomers,
    handleGetUsers,
    handleGetGiftCardUsers,
    handleSegmentList,
  };
};
